import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Edit2, RefreshCw, Save, X } from 'lucide-react';
import apiService, { AdminTrialConfig } from '../../services/api';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface TrialConfigManagementProps {
  onNotify?: (message: string, type: ToastType) => void;
}

interface TrialConfigFormData {
  trial_days: string;
  trial_credits: string;
  topup_enabled: boolean;
}

const initialTrialConfigForm: TrialConfigFormData = {
  trial_days: '',
  trial_credits: '',
  topup_enabled: true,
};

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

const parseBooleanValue = (value: AdminTrialConfig['trial_topup_enabled']) =>
  value === true || String(value).toLowerCase() === 'true';

const normalizeTrialConfig = (config: AdminTrialConfig): TrialConfigFormData => ({
  trial_days: String(config.default_trial_days ?? ''),
  trial_credits: String(config.default_trial_credits ?? ''),
  topup_enabled: parseBooleanValue(config.trial_topup_enabled),
});

const parseWholeNumber = (
  value: string,
  label: string,
  min: number,
  max: number,
) => {
  const trimmedValue = value.trim();
  const parsedValue = Number(trimmedValue);

  if (
    trimmedValue === '' ||
    !Number.isInteger(parsedValue) ||
    parsedValue < min ||
    parsedValue > max
  ) {
    throw new Error(`${label} must be a whole number from ${min} to ${max}.`);
  }

  return parsedValue;
};

const TrialConfigManagement: React.FC<TrialConfigManagementProps> = ({
  onNotify,
}) => {
  const [config, setConfig] = useState<AdminTrialConfig | null>(null);
  const [form, setForm] = useState<TrialConfigFormData>(initialTrialConfigForm);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const notify = useCallback(
    (message: string, type: ToastType = 'success') => {
      if (onNotify) {
        onNotify(message, type);
      }
    },
    [onNotify],
  );

  const loadTrialConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    setFormError(null);

    try {
      const response = await apiService.getAdminTrialConfig();
      setConfig(response);
      setForm(normalizeTrialConfig(response));
    } catch (error) {
      setError(getErrorMessage(error, 'Failed to load trial configuration'));
      setConfig(null);
      setForm(initialTrialConfigForm);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTrialConfig();
  }, [loadTrialConfig]);

  const isDirty = useMemo(() => {
    if (!config) {
      return false;
    }

    const normalizedConfig = normalizeTrialConfig(config);
    return (
      form.trial_days !== normalizedConfig.trial_days ||
      form.trial_credits !== normalizedConfig.trial_credits ||
      form.topup_enabled !== normalizedConfig.topup_enabled
    );
  }, [config, form]);

  const handleFormChange = (
    field: keyof TrialConfigFormData,
    value: string | boolean,
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const resetForm = () => {
    if (!config) {
      return;
    }

    setForm(normalizeTrialConfig(config));
    setFormError(null);
  };

  const openEditModal = () => {
    if (!config) {
      return;
    }

    setForm(normalizeTrialConfig(config));
    setFormError(null);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    if (submitting) {
      return;
    }

    setShowEditModal(false);
    resetForm();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setFormError(null);

    try {
      const trialDays = parseWholeNumber(form.trial_days, 'Trial days', 1, 90);
      const trialCredits = parseWholeNumber(
        form.trial_credits,
        'Trial credits',
        10,
        1000,
      );

      const response = await apiService.updateAdminTrialConfig({
        trial_days: trialDays,
        trial_credits: trialCredits,
        topup_enabled: form.topup_enabled,
      });

      setConfig(response);
      setForm(normalizeTrialConfig(response));
      setShowEditModal(false);
      notify('Trial configuration updated successfully.', 'success');
    } catch (error) {
      setFormError(getErrorMessage(error, 'Failed to update trial configuration'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[280px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Trial Configuration</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={openEditModal}
            disabled={!config || submitting}
            className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
          >
            <Edit2 className="h-4 w-4" />
            Edit
          </button>
          <button
            type="button"
            onClick={loadTrialConfig}
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {config && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-sm font-medium text-gray-500">Default Trial Days</div>
            <div className="mt-2 text-2xl font-bold text-gray-900">
              {config.default_trial_days}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-sm font-medium text-gray-500">Default Trial Credits</div>
            <div className="mt-2 text-2xl font-bold text-green-600">
              {config.default_trial_credits}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-sm font-medium text-gray-500">Trial Top-up</div>
            <div className="mt-2">
              <span
                className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${
                  parseBooleanValue(config.trial_topup_enabled)
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {parseBooleanValue(config.trial_topup_enabled) ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-50 px-4 py-8">
          <div className="mx-auto max-w-2xl overflow-hidden rounded-lg bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Edit Trial Config</h3>
              </div>
              <button
                type="button"
                onClick={closeEditModal}
                disabled={submitting}
                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-60"
                aria-label="Close edit trial config"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form className="space-y-5 px-6 py-5" onSubmit={handleSubmit}>
              {formError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Trial Days *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="90"
                    step="1"
                    required
                    value={form.trial_days}
                    onChange={(event) => handleFormChange('trial_days', event.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Allowed range: 1 to 90 days.</p>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Trial Credits *
                  </label>
                  <input
                    type="number"
                    min="10"
                    max="1000"
                    step="1"
                    required
                    value={form.trial_credits}
                    onChange={(event) =>
                      handleFormChange('trial_credits', event.target.value)
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Allowed range: 10 to 1000 credits.</p>
                </div>

                <div className="md:col-span-2">
                  <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
                    <input
                      id="trialTopupEnabled"
                      type="checkbox"
                      checked={form.topup_enabled}
                      onChange={(event) =>
                        handleFormChange('topup_enabled', event.target.checked)
                      }
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label htmlFor="trialTopupEnabled" className="ml-2 text-sm text-gray-700">
                      Allow companies to top-up credits during trial
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-3 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={!isDirty || submitting}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={closeEditModal}
                  disabled={submitting}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !!error}
                  className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                >
                  <Save className="h-4 w-4" />
                  {submitting ? 'Saving...' : 'Save Config'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrialConfigManagement;
